import { Info, Users } from "lucide-react";
import { TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

import { Card } from "@/components/ui/card";
import { Link } from "@tanstack/react-router";
import { Tooltip as UITooltip } from "@/components/ui/tooltip";
import { formatNumber } from "@/utils/format";

interface LeaderboardCreator {
  creatorId: string;
  name: string;
  avatarUrl: string;
  totalViews: number;
  totalLikes: number;
  totalVideos: number;
}

interface LeaderboardProps {
  isLoading: boolean;
  creators?: LeaderboardCreator[];
}

export function Leaderboard({ isLoading, creators }: LeaderboardProps) {
  return (
    <Card className="p-6 min-h-[500px]">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <h3 className="text-lg font-semibold text-primary">Top Creators</h3>
          <UITooltip>
            <TooltipTrigger>
              <Info className="h-4 w-4 text-primary/60 hover:text-primary/80" />
            </TooltipTrigger>
            <TooltipContent>
              <p className="max-w-xs">
                Shows the top creators based on their total views and engagement
                metrics.
              </p>
            </TooltipContent>
          </UITooltip>
        </div>
        <p className="text-sm text-primary/60 mb-4">
          Overview of the most active creators in the selected campaigns.
        </p>
      </div>

      {isLoading ? (
        <div className="h-96 bg-gray-100 rounded animate-pulse flex items-center justify-center">
          <div className="text-primary/60">Loading leaderboard...</div>
        </div>
      ) : creators && creators.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left py-3 px-4 font-semibold text-primary">
                  Rank
                </th>
                <th className="text-left py-3 px-4 font-semibold text-primary">
                  Creator
                </th>
                <th className="text-right py-3 px-4 font-semibold text-primary">
                  Total Views
                </th>
                <th className="text-right py-3 px-4 font-semibold text-primary">
                  Total Likes
                </th>
                <th className="text-right py-3 px-4 font-semibold text-primary">
                  Videos
                </th>
              </tr>
            </thead>
            <tbody>
              {creators.map((creator, index) => (
                <tr
                  key={creator.creatorId}
                  className="border-b border-gray-100 dark:border-gray-700"
                >
                  <td className="py-3 px-4 text-left text-primary">
                    <span className="text-primary/60">
                      {index === 0
                        ? "🥇"
                        : index === 1
                          ? "🥈"
                          : index === 2
                            ? "🥉"
                            : `#${index + 1}`}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-left text-primary">
                    <div className="flex items-center gap-3">
                      <img
                        src={creator.avatarUrl}
                        alt={creator.name}
                        className="h-8 w-8 rounded-full object-cover"
                      />
                      <Link
                        to="/creators/$creatorId"
                        params={{ creatorId: creator.creatorId }}
                        className="font-medium hover:underline text-primary"
                      >
                        {creator.name}
                      </Link>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-right text-primary">
                    {formatNumber(creator.totalViews)}
                  </td>
                  <td className="py-3 px-4 text-right text-primary">
                    {formatNumber(creator.totalLikes)}
                  </td>
                  <td className="py-3 px-4 text-right text-primary">
                    {formatNumber(creator.totalVideos)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12">
          <Users className="h-12 w-12 text-primary/40 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-primary mb-2">
            No Creators Found
          </h3>
          <p className="text-sm text-primary/60">
            No creators have posted approved videos in the selected campaigns.
          </p>
        </div>
      )}
    </Card>
  );
}
